import { useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { AuthenticationError, trackError } from '#api/errors.ts';
import { useSuspenseTOTPAuthenticatorsQuery } from '#api/totp-authenticators.queries.ts';
import { useSuspenseWebAuthnAuthenticatorsQuery } from '#api/webauthn-authenticators.queries.ts';
import { ErrorMessage } from '#components/atoms/forms/index.ts';
import { ConfirmDialog } from '#components/patterns/confirmations/confirm-dialog/ConfirmDialog.tsx';
import { useAppLocation } from '#hooks/useAppLocation.ts';
import { useDeleteWebAuthnMutation } from './useDeleteWebAuthnMutation.ts';

export function DeleteWebAuthnModal() {
  const { webauthnId } = useParams() as { webauthnId: string };
  const {
    data: { data: credentials },
  } = useSuspenseWebAuthnAuthenticatorsQuery();
  const {
    data: { data: totpData },
  } = useSuspenseTOTPAuthenticatorsQuery();
  const navigate = useNavigate();
  const location = useAppLocation();
  const [globalErrorMessage, setGlobalErrorMessage] = useState<string | undefined>();

  const deleteMutation = useDeleteWebAuthnMutation({
    onSuccess() {
      returnToUserSettings();
    },
    onError: (err) => {
      if (err instanceof AuthenticationError && err.code === 'ERR_2FA_RENEWAL_REQUIRED') {
        void navigate('/auth/mfa/renew', { state: { from: location } });
      } else {
        trackError(err);
        setGlobalErrorMessage('There was a problem deleting the passkey. Please try again or contact support.');
      }
    },
  });

  function returnToUserSettings() {
    void navigate('..', { replace: true });
  }

  const totalAuthenticators = credentials.length + totpData.length;

  if (totalAuthenticators < 2) {
    return (
      <ConfirmDialog
        title="Cannot delete passkey"
        acceptButtonText="Close"
        onCancel={returnToUserSettings}
        onAccept={returnToUserSettings}
        shouldRestoreFocusViaFocusScope={true}
      >
        <p className="p mt-0">Deleting your last authenticator would prevent you from logging in.</p>
        <p className="p mb-0">Add another authenticator before deleting this one.</p>
      </ConfirmDialog>
    );
  }

  return (
    <ConfirmDialog
      title="Delete passkey?"
      disableAccept={deleteMutation.isPending}
      acceptButtonText="Delete"
      onCancel={returnToUserSettings}
      onAccept={() => {
        deleteMutation.mutate(webauthnId);
      }}
      shouldRestoreFocusViaFocusScope={true}
    >
      <p className="p mt-0 mb-0">
        You will no longer be able to log in using this passkey. Please ensure you have another authenticator that
        works.
      </p>
      {globalErrorMessage && <ErrorMessage className="mt-16">{globalErrorMessage}</ErrorMessage>}
    </ConfirmDialog>
  );
}
